import type { Session } from "./schemas/session.js";
import { calendarWeeksAgo, DEFAULT_WEEK_START_DAY } from "./weekWindow.js";

// PG-58: the consistency streak, how many calendar weeks in a row hold at
// least one session. Weeks start on the athlete's weekStartDay, like the
// Rhythm bars (PG-18). The current week is still open: without a session yet
// it does not break the run, the streak is the one ending last week.
// PG-64 fetta 2: the widget's columns are the sessions per week
// (computeWeeklyCounts, the Rhythm's own), so the hit/miss list is gone.

export type WeekStreak = {
  /** Consecutive weeks up to now: this week included if it has a session. */
  current: number;
  /** The longest run ever, `current` included. */
  best: number;
  currentWeekHasSession: boolean;
};

/** Sessions after `nowMs` and unparseable dates are ignored. */
export function computeWeekStreak(
  sessions: ReadonlyArray<Pick<Session, "startedAt">>,
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): WeekStreak {
  const hit = new Set<number>();
  for (const s of sessions) {
    const ago = calendarWeeksAgo(
      new Date(s.startedAt).getTime(),
      nowMs,
      weekStartDay,
    );
    if (ago >= 0) hit.add(ago); // drops the future and NaN
  }

  const currentWeekHasSession = hit.has(0);
  let current = 0;
  for (let ago = currentWeekHasSession ? 0 : 1; hit.has(ago); ago++) current++;

  let best = 0;
  let run = 0;
  let prev = Number.NaN;
  for (const ago of [...hit].sort((a, b) => a - b)) {
    run = ago === prev + 1 ? run + 1 : 1;
    if (run > best) best = run;
    prev = ago;
  }

  return { current, best, currentWeekHasSession };
}
